import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import * as I from 'immutable';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

type ShopProps = {
  planType: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  baseUnit: number;
  selectCGI: boolean;
  section125: boolean;
  riderCodeList: I.List<string>;
  riderAmounts: I.List<number>;
};

export class InvalidNewYorkShop {
  public readonly key = 'InvalidNewYorkShop';

  constructor(
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly baseUnit: number,
    public readonly selectCGI: boolean,
    public readonly section125: boolean,
    public readonly riderCodeList: I.List<string>,
    public readonly riderAmounts: I.List<number>
  ) {}
}

export class ValidNewYorkShop {
  public static createApp({
    planType,
    premium,
    calculatedPremium,
    cwa,
    baseUnit,
    selectCGI,
    section125,
    riderCodeList,
    riderAmounts,
  }: ShopProps) {
    return this.create(
      planType,
      premium,
      calculatedPremium,
      cwa,
      baseUnit,
      selectCGI,
      section125,
      riderCodeList,
      riderAmounts
    );
  }

  public static create(
    planType: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    baseUnit: number,
    selectCGI: boolean,
    section125: boolean,
    riderCodeList: I.List<string>,
    riderAmounts: I.List<number>
  ) {
    return isNonEmptyString(planType)
      ? new ValidNewYorkShop(
          planType,
          premium,
          calculatedPremium,
          cwa,
          baseUnit,
          selectCGI,
          section125,
          riderCodeList,
          riderAmounts
        )
      : new InvalidNewYorkShop(
          planType,
          premium,
          calculatedPremium,
          cwa,
          baseUnit,
          selectCGI,
          section125,
          riderCodeList,
          riderAmounts
        );
  }

  public readonly key = 'ValidNewYorkShop';
  constructor(
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly baseUnit: number,
    public readonly selectCGI: boolean,
    public readonly section125: boolean,
    public readonly riderCodeList: I.List<string>,
    public readonly riderAmounts: I.List<number>
  ) {}
}

export type NewYorkShop = InvalidNewYorkShop | ValidNewYorkShop;

export type NewYorkShopOptions<TReturn> = ToPartialMatchOptions<NewYorkShop, 'key', TReturn>;

export const matchNewYorkShop = <TReturn>(options: NewYorkShopOptions<TReturn>) => (app: NewYorkShop): TReturn => {
  switch (app.key) {
    case 'InvalidNewYorkShop':
      return options.InvalidNewYorkShop ? options.InvalidNewYorkShop(app) : options.default(app);
    case 'ValidNewYorkShop':
      return options.ValidNewYorkShop ? options.ValidNewYorkShop(app) : options.default(app);
  }
};

export const EmptyNewYorkShop = new InvalidNewYorkShop(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  -1,
  false,
  false,
  I.List<string>(),
  I.List<number>()
);

export const changePlanType = (planType: string) => (app: NewYorkShop) =>
  ValidNewYorkShop.createApp({...app, planType: NonEmptyString.create(planType)});
export const changePremium = (premium: string) => (app: NewYorkShop) =>
  ValidNewYorkShop.createApp({...app, premium: NonEmptyString.create(premium)});
export const changeCalculatedPremium = (calculatedPremium: string) => (app: NewYorkShop) =>
  ValidNewYorkShop.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});
export const changeCWA = (cwa: string) => (app: NewYorkShop) =>
  ValidNewYorkShop.createApp({...app, cwa: NonEmptyString.create(cwa)});
export const changeBaseUnit = (baseUnit: number) => (app: NewYorkShop) =>
  ValidNewYorkShop.createApp({...app, baseUnit});
export const changeSection = (section: boolean) => (app: NewYorkShop) =>
  ValidNewYorkShop.createApp({...app, section125: section});
export const changeCGI = (cgi: boolean) => (app: NewYorkShop) => ValidNewYorkShop.createApp({...app, selectCGI: cgi});

export const changeRiderCodes = (riderCode: string, index: number) => (app: NewYorkShop) => {
  const existingList: I.List<string> = app.riderCodeList;
  const newList = existingList.set(index, riderCode);
  return ValidNewYorkShop.createApp({...app, riderCodeList: newList});
};

export const changeRiderAmount = (riderAmt: number, index: number) => (app: NewYorkShop) => {
  const existingList: I.List<number> = app.riderAmounts;
  const newList = existingList.set(index, riderAmt);

  return ValidNewYorkShop.createApp({...app, riderAmounts: newList});
};
